'use client';

import type { ClassValue } from 'clsx';
import type { ReactNode } from 'react';

import {
  ProductBandDisclosure,
  ProductBandDisclosureChevron,
  ProductBandDisclosureContent,
  ProductBandDisclosureHeader,
  ProductBandDisclosureTrigger,
  ProductBandTitle,
  useProductBandDisclosure,
} from '@vs-shop/commerce-ui/productBand';
import { Heading } from '@vs-shop/ui/heading';
import { clsxm } from '@vs-shop/utils';

type Props = {
  // oxlint-disable-next-line vspoint-effect/branded-component-props -- DOM anchor id for scroll targets, not an entity id
  id: string;
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: ClassValue;
};

/**
 * A product-page band, folded, as this storefront composes it: the title as the
 * control, a chevron for its state, and the body behind it.
 *
 * The client half of every band on the page — the description, the parameters
 * and the accessories menu all run through here, so what each of them passes is
 * copy and an anchor. The body arrives as `children` and stays server-rendered.
 *
 * The `<h2>` is spelled here because this is the call site that knows: these
 * bands sit under the product's own name on the product page, and each is a
 * second-level entry in that page's outline.
 */
export default function ProductSectionCollapsible({
  id,
  title,
  children,
  defaultOpen = false,
  className,
}: Props) {
  const { isOpen, setOpen, triggerRef } = useProductBandDisclosure({ anchor: id, defaultOpen });

  return (
    <ProductBandDisclosure open={isOpen} onOpenChange={setOpen} className={clsxm(className)}>
      <ProductBandDisclosureTrigger ref={triggerRef}>
        <ProductBandDisclosureHeader>
          <ProductBandTitle render={<Heading level={2} />} className="whitespace-nowrap">
            {title}
          </ProductBandTitle>
          <ProductBandDisclosureChevron />
        </ProductBandDisclosureHeader>
      </ProductBandDisclosureTrigger>

      <ProductBandDisclosureContent role="region" aria-labelledby={id}>
        {children}
      </ProductBandDisclosureContent>
    </ProductBandDisclosure>
  );
}
